import {
	DefineQueryOptions,
	EntryKey,
	useQuery,
	useQueryCache,
	UseQueryOptions,
	UseQueryReturn,
} from "@pinia/colada";
import { ScopeType, UserScope } from "@soft-db/monitoring-api-client";
import { storeToRefs } from "pinia";
import { ShallowRef } from "vue";
import { useQueryManager } from "../stores/query-manager";
import { useScopeStore } from "../stores/scope";

export type QueryOptions = {
	enabled?: MaybeRefOrGetter<boolean>;
};

export function useQueryEnabled(
	...ids: MaybeRefOrGetter<string | null | boolean>[]
): ComputedRef<boolean> {
	const { enabled } = storeToRefs(useQueryManager());

	return computed(() => enabled.value && ids.every((id) => !!toValue(id)));
}

export function composeCacheEffects<T>(...effects: ((value: T) => void)[]) {
	return (value: T) => {
		effects.forEach((fn) => fn(value));
	};
}

export function removeUndefinedFromListQuery<T>(
	_data: ShallowRef<T[] | undefined>
): ShallowRef<T[]> {
	const data = shallowRef<T[]>([]);
	watchImmediate(_data, () => {
		if (_data.value != null) {
			data.value = _data.value;
		}
	});

	return data;
}

export type ObjectIdentifier = { id: string; name?: string };

export function arrayToMap<T extends ObjectIdentifier>(elements: T[]) {
	return elements.reduce((map, el) => {
		map.set(el.id, el);
		return map;
	}, new Map<string, T>());
}

export function indexedQuery<T extends ObjectIdentifier>(
	query: UseQueryReturn<T[]>
): UseQueryReturn<Map<string, T>> {
	return {
		...query,
		data: computed(() => (query.data.value ? arrayToMap(query.data.value) : undefined)),
	} as any;
}

export type CacheEntry<T> = {
	data: T;
	when: Date;
};

export function useCachedEntries<T extends ObjectIdentifier>(key: EntryKey) {
	const cache = useQueryCache();

	return computed(() => {
		const entries = cache.getEntries({ key: key });

		const cacheEntries = new Map<string, CacheEntry<T>>();
		for (const entry of entries) {
			const { when } = entry;
			let { data } = entry.state.value;

			if (!Array.isArray(data)) {
				data = [data];
			}

			for (const d of data as unknown[]) {
				if (d != null && typeof d === "object" && "id" in d && typeof d.id === "string") {
					cacheEntries.set(d.id, {
						when: new Date(when),
						data: d as any,
					});
				}
			}
		}

		return cacheEntries;
	});
}

export function extractQueryKey<T>(factory: (opts: T) => DefineQueryOptions, opts: Omit<T, "api">) {
	return factory(opts as any).key;
}

export function keyWithScope(key: EntryKey, scope: UserScope) {
	return [...key, scope.type, scope.id];
}

function isScopeType(value: any): value is ScopeType {
	return (
		value != null &&
		typeof value === "string" &&
		(value === "space" || value === "distributor" || value === "none")
	);
}

export function scopeFromKey(key: EntryKey): UserScope | null {
	const [maybeScopeType, maybeScopeId] = [key.at(-2), key.at(-1)];

	if (isScopeType(maybeScopeType) && maybeScopeId != null && typeof maybeScopeId === "string") {
		return {
			type: maybeScopeType,
			id: maybeScopeId,
		};
	}

	return null;
}

export function useScopedQuery<
	TData,
	TError = Error,
	TDataInitial extends TData | undefined = undefined,
>(
	options: UseQueryOptions<TData, TError, TDataInitial>
): UseQueryReturn<TData, TError, TDataInitial> {
	const { scope } = storeToRefs(useScopeStore());

	return useQuery({
		...options,
		key: () => keyWithScope(toValue(options.key), scope.value),
	});
}

export type InvalidatorOptions = {
	keys?: EntryKey[];
	// Used to invalidate keys that match the base provided key, but are in other scope.
	otherScopeKeys?: EntryKey[];
};

export function useInvalidator() {
	const { scope } = storeToRefs(useScopeStore());

	const queryCache = useQueryCache();

	return (options: InvalidatorOptions) => {
		const { keys = [], otherScopeKeys: scopedKeys = [] } = options;

		for (const key of keys) {
			queryCache.invalidateQueries({ key });
		}

		for (const key of scopedKeys) {
			for (const entry of queryCache.getEntries({ key: key })) {
				const queryScope = scopeFromKey(entry.key);
				if (queryScope == null) {
					continue;
				}

				if (queryScope.type === scope.value.type && queryScope.id === scope.value.id) {
					continue;
				}

				queryCache.invalidate(entry);
			}
		}
	};
}

export function useScopeDataUpdater() {
	const { scope } = storeToRefs(useScopeStore());

	const queryCache = useQueryCache();

	return (key: EntryKey, data: any) => {
		const scopedKey = keyWithScope(key, scope.value);

		queryCache.setQueryData(scopedKey, data);
	};
}
